import { all, take, takeEvery, select, apply, put, call } from 'redux-saga/effects';

import {
    FETCH_ORGANIZATION_SETTINGS_ATTEMPT,
    FETCH_ORGANIZATION_SETTINGS_SUCCEEDED,

    UPDATE_AUTO_PROGRESS_ATTEMPT,
    UPDATE_AUTO_PROGRESS_SUCCEEDED,

    UPDATE_DEFAULT_REST,
    UPDATE_DEFAULT_NOTES,
    UPDATE_DEFAULT_AUTOREGULATION,
    UPDATE_DEFAULT_SENSOR_TRACKING,
    UPDATE_DEFAULT_AMRAP,
    UPDATE_DEFAULT_SET_COUNT_FIXED,
    UPDATE_DEFAULT_WEIGHT_UNIT,
    UPDATE_DEFAULT_AUTOREGULATION_QUANTIFIER,
    UPDATE_DEFAULT_AUTOREGULATION_METRIC,
    SAVE_DEFAULTS_ATTEMPT,
    SAVE_DEFAULTS_SUCCEEDED,

    ATTEMPT_UPLOAD_ORGANIZATION_LOGO,
    UPLOAD_ORGANIZATION_LOGO_SUCCEEDED,
    UPLOAD_ORGANIZATION_LOGO_FAILED,


    FETCH_ACCOUNT_SUCCEEDED,
} from 'shared_redux/actions';
import API from 'services/settings';
import OrganizationsAPI from 'services/organizations';
import RolesAPI from 'services/roles';
import { handleError, displayErrorAlert } from 'services/alerts';

import * as AccountsSelectors from 'shared_redux/selectors/models/accounts';
import * as SettingsSelectors from 'shared_redux/selectors/view/settings';
import * as OrganizationSwitcherSelectors from 'shared_redux/selectors/view/organization_switcher';

// Helper function to get the effective organization ID
function* getOrganizationId() {
    // First try to get the organization ID from the organization switcher state
    const organizationId = yield select(OrganizationSwitcherSelectors.getEffectiveOrganizationId);
    
    if (organizationId) {
        return organizationId;
    }
    
    // If no organization ID is available, wait for account to be fetched and use its organization_id
    yield take(FETCH_ACCOUNT_SUCCEEDED);
    const account = yield select(AccountsSelectors.getAccount);
    return account.organization_id;
}

export default function* SettingsSaga() {
    yield all([
        takeEvery(FETCH_ORGANIZATION_SETTINGS_ATTEMPT, fetchSettings),
        takeEvery(UPDATE_AUTO_PROGRESS_ATTEMPT, updateSettings),

        takeEvery(UPDATE_DEFAULT_REST, saveDefaults),
        takeEvery(UPDATE_DEFAULT_NOTES, saveDefaults),
        takeEvery(UPDATE_DEFAULT_AUTOREGULATION, saveDefaults),
        takeEvery(UPDATE_DEFAULT_SENSOR_TRACKING, saveDefaults),
        takeEvery(UPDATE_DEFAULT_AMRAP, saveDefaults),
        takeEvery(UPDATE_DEFAULT_SET_COUNT_FIXED, saveDefaults),
        takeEvery(UPDATE_DEFAULT_WEIGHT_UNIT, saveDefaults),
        takeEvery(UPDATE_DEFAULT_AUTOREGULATION_QUANTIFIER, saveDefaults),
        takeEvery(UPDATE_DEFAULT_AUTOREGULATION_METRIC, saveDefaults),

        takeEvery(SAVE_DEFAULTS_ATTEMPT, saveDefaults),
        takeEvery(ATTEMPT_UPLOAD_ORGANIZATION_LOGO, uploadOrganizationLogo),
    ]);
};

function *fetchSettings(action) {
    try {
        const organizationId = yield call(getOrganizationId);
        const payload = yield apply(API, API.getSettings, [organizationId]);
        yield put({
            type: FETCH_ORGANIZATION_SETTINGS_SUCCEEDED,
            payload,
        });
    } catch(error) {
        yield handleError(error, 'Error: Unable to fetch settings');
    }
}

function *updateSettings(action) {
    try {
        const organizationId = yield call(getOrganizationId);
        const payload = yield apply(API, API.updateSettings, [organizationId, action.payload]);
        yield put({
            type: UPDATE_AUTO_PROGRESS_SUCCEEDED,
            payload,
        });
    } catch(error) {
        yield handleError(error, 'Error: Unable to save settings');
    }
}

function *saveDefaults(action) {
    try {
        const organizationId = yield call(getOrganizationId);

        // get settings
        const isValid = yield select(SettingsSelectors.getSettingsValid);
        if (!isValid) {
            // error
            displayErrorAlert(`The defaults you entered are invalid. Please check the fields and try again.`);
        } else {
            // success
            const settings = yield select(SettingsSelectors.getOrganizationSettings);
            const payload = yield apply(API, API.updateSettings, [organizationId, settings]);
            yield put({
                type: SAVE_DEFAULTS_SUCCEEDED,
                payload,
            });
        }
    } catch(error) {
        yield handleError(error, 'Error: Unable to save settings');
    }
}

function *uploadOrganizationLogo(action) {
    try {
        let organizationId = yield select(OrganizationSwitcherSelectors.getEffectiveOrganizationId);
        
        if (!organizationId) {
            let account = yield select(AccountsSelectors.getAccount);
            if (!account) {
                yield take(FETCH_ACCOUNT_SUCCEEDED);
                account = yield select(AccountsSelectors.getAccount);
            }
            organizationId = account.organization_id;
        }

        // Step 1: Get the signed URL from the backend
        const uploadUrlResponse = yield apply(OrganizationsAPI, OrganizationsAPI.generateLogoUploadUrl, [organizationId]);
        
        // Step 2: Upload the image to S3
        const s3Response = yield fetch(uploadUrlResponse.upload_url, {
            method: 'PUT',
            body: action.payload,
            headers: {
                'Content-Type': 'image/png',
            },
        });

        if (!s3Response.ok) {
            throw new Error(`S3 upload failed with status ${s3Response.status}: ${s3Response.statusText}`);
        }

        // Step 3: Confirm the upload with the backend to update the database
        const confirmResponse = yield apply(OrganizationsAPI, OrganizationsAPI.confirmLogoUpload, [
            organizationId,
            uploadUrlResponse.logo_url,
            uploadUrlResponse.upload_key
        ]);

        // Force refresh of image by adding timestamp to URL
        const updatedOrganization = {
            ...confirmResponse,
            logo_url: confirmResponse.logo_url ? `${confirmResponse.logo_url}?${new Date().getTime()}` : null
        };

        yield put({
            type: UPLOAD_ORGANIZATION_LOGO_SUCCEEDED,
            payload: updatedOrganization,
        });
    } catch(error) {
        yield put({
            type: UPLOAD_ORGANIZATION_LOGO_FAILED,
            payload: error.message || 'Failed to upload organization logo',
        });
        yield handleError(error, 'Error: Unable to upload organization logo');
    }
}

